import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";

// Patch history navigation to dispatch "popstate" natively, resolving wouter's query parameters tracking issue.
if (typeof window !== "undefined") {
 const originalPushState = window.history.pushState;
 const originalReplaceState = window.history.replaceState;

 window.history.pushState = function (...args) {
 originalPushState.apply(this, args);
 window.dispatchEvent(new Event("popstate"));
 };

 window.history.replaceState = function (...args) {
 originalReplaceState.apply(this, args);
 window.dispatchEvent(new Event("popstate"));
 };
}

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
 if (!(error instanceof TRPCClientError)) return;
 if (typeof window === "undefined") return;

 const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

 if (!isUnauthorized) return;

 window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
 if (event.type === "updated" && event.action.type === "error") {
 const error = event.query.state.error;
 redirectToLoginIfUnauthorized(error);
 console.error("[API Query Error]", error);
 }
});

queryClient.getMutationCache().subscribe(event => {
 if (event.type === "updated" && event.action.type === "error") {
 const error = event.mutation.state.error;
 redirectToLoginIfUnauthorized(error);
 console.error("[API Mutation Error]", error);
 }
});

const trpcClient = trpc.createClient({
 links: [
 httpLink({
 url: "/api/trpc",
 transformer: superjson,
 fetch(input, init) {
    const port = typeof window !== "undefined" ? window.location.port : "";
    const host = typeof window !== "undefined" ? window.location.host : "";
    const headers = new Headers(init?.headers || {});
    if (port) headers.set("x-tenant-port", port);
    if (host) headers.set("x-tenant-host", host);
    return globalThis.fetch(input, {
      ...(init ?? {}),
      headers,
      credentials: "include",
    });
  },
 }),
 ],
});

// Dynamically load Umami analytics if configured and avoiding relative dev server path issues
const analyticsEndpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
const websiteId = import.meta.env.VITE_ANALYTICS_WEBSITE_ID;

if (analyticsEndpoint && websiteId && typeof window !== "undefined") {
 const isAbsolute = analyticsEndpoint.startsWith("http://") || analyticsEndpoint.startsWith("https://");
 const isProduction = import.meta.env.PROD;
 
 if (isAbsolute || (isProduction && analyticsEndpoint !== "analytics")) {
 const script = document.createElement("script");
 script.defer = true;
 script.src = isAbsolute ? `${analyticsEndpoint}/umami` : `/${analyticsEndpoint}/umami`;
 script.setAttribute("data-website-id", websiteId);
 document.head.appendChild(script);
 }
}

createRoot(document.getElementById("root")!).render(
 <trpc.Provider client={trpcClient} queryClient={queryClient}>
 <QueryClientProvider client={queryClient}>
 <App />
 </QueryClientProvider>
 </trpc.Provider>
);
